import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { SensorData } from '../types';
import { formatSensorValue, calculateFuelPercentage, formatTimestamp } from '../utils/formatters';
import { Droplets, AlertTriangle, CheckCircle, MapPin, BarChart3 } from 'lucide-react';
import SensorChart from './SensorChart';

interface FuelTankProps {
  sensor: SensorData;
}

const FuelTank: React.FC<FuelTankProps> = ({ sensor }) => {
  const [showChart, setShowChart] = useState(false);
  const value = sensor.current_value?.value || 0;
  const minValue = sensor.display_config?.min_value || 0;
  const maxValue = sensor.display_config?.max_value || 100;
  const percentage = sensor.current_value?.value ? calculateFuelPercentage(value, minValue, maxValue) : 0;
  
  const isOnline = sensor.device_info?.status || false;
  const lastUpdate = formatTimestamp(sensor.current_value?.timestamp || null);
  const hasData = sensor.current_value?.value !== null && sensor.current_value?.value !== undefined;
  
  const getFuelColor = (percentage: number) => {
    if (!hasData) return 'from-gray-300 to-gray-400'; // Gray for no data
    if (percentage > 50) return 'from-green-400 to-green-600';
    if (percentage > 25) return 'from-yellow-400 to-yellow-600';
    return 'from-red-400 to-red-600';
  };

  const getStatusIcon = () => {
    if (!isOnline || !hasData) return <AlertTriangle className="w-5 h-5 text-red-500" />;
    if (percentage < 20) return <AlertTriangle className="w-5 h-5 text-yellow-500" />;
    return <CheckCircle className="w-5 h-5 text-green-500" />;
  };

  return (
    <>
      <div className="sensor-card cursor-pointer" onClick={() => setShowChart(true)}>
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-2">
            <Droplets className="w-6 h-6 text-blue-500" />
            <h3 className="text-lg font-semibold text-gray-900">{sensor.name}</h3>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={(e) => {
                e.stopPropagation();
                setShowChart(true);
              }}
              className="p-2 bg-blue-100 text-blue-600 rounded-lg hover:bg-blue-200 transition-colors"
              title="View Chart"
            >
              <BarChart3 className="w-4 h-4" />
            </button>
            {getStatusIcon()}
            <span className={`text-sm ${
              isOnline && hasData ? 'text-green-600' : 
              isOnline && !hasData ? 'text-yellow-600' : 
              'text-red-600'
            }`}>
              {isOnline && hasData ? 'Online' : 
               isOnline && !hasData ? 'No Data' : 
               'Offline'}
            </span>
          </div>
        </div>

        {/* Fuel Tank Visual */}
        <div className="relative mx-auto mb-6" style={{ width: '200px', height: '300px' }}>
          {/* Tank Outline */}
          <div className="absolute inset-0 bg-gray-200 rounded-lg border-4 border-gray-300 overflow-hidden">
            {/* Fuel Level */}
            <motion.div
              className={`absolute bottom-0 left-0 w-full bg-gradient-to-t ${getFuelColor(percentage)} liquid-animation`}
              initial={{ height: 0 }}
              animate={{ height: `${percentage}%` }}
              transition={{ duration: 2, ease: 'easeOut' }}
            />
            
            {/* Tank Markings */}
            <div className="absolute inset-0 pointer-events-none">
              {[25, 50, 75].map((mark) => (
                <div
                  key={mark}
                  className="absolute left-0 right-0 border-t border-gray-400"
                  style={{ bottom: `${mark}%` }}
                >
                  <span className="absolute right-2 -top-2 text-xs text-gray-600 bg-white px-1 rounded">
                    {mark}%
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Tank Cap */}
          <div className="absolute -top-2 left-1/2 transform -translate-x-1/2 w-12 h-4 bg-gray-400 rounded-t-lg"></div>
          
          {/* Tank Base */}
          <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 w-16 h-4 bg-gray-400 rounded-b-lg"></div>
        </div>

        {/* Value Display */}
        <div className="text-center mb-4">
          <div className="sensor-value text-4xl font-mono">
            {hasData ? formatSensorValue(sensor) : 'No Data'}
          </div>
          <div className="text-lg text-gray-600">
            {hasData ? `${percentage.toFixed(1)}% Full` : 'Sensor Offline'}
          </div>
        </div>

        {/* Additional Info */}
        <div className="space-y-2 text-sm text-gray-600">
          <div className="flex justify-between">
            <span>Device:</span>
            <span className="font-medium">{sensor.device_info?.name || 'Unknown'}</span>
          </div>
          <div className="flex justify-between">
            <span>Last Update:</span>
            <span className="font-medium">{lastUpdate}</span>
          </div>
          <div className="flex justify-between">
            <span>Group:</span>
            <span className="font-medium">{sensor.group.name}</span>
          </div>
          {sensor.location && (
            <div className="flex justify-between">
              <span>Location:</span>
              <span className="font-medium flex items-center">
                <MapPin className="w-3 h-3 mr-1" />
                {sensor.location}
              </span>
            </div>
          )}
        </div>

        {/* Low Fuel Warning */}
        {hasData && percentage < 20 && (
          <motion.div
            className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
          >
            <div className="flex items-center space-x-2">
              <AlertTriangle className="w-4 h-4 text-yellow-600" />
              <span className="text-yellow-800 text-sm font-medium">Low Fuel Warning</span>
            </div>
          </motion.div>
        )}

        {/* No Data Warning */}
        {!hasData && (
          <motion.div
            className="mt-4 p-3 bg-gray-50 border border-gray-200 rounded-lg"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
          >
            <div className="flex items-center space-x-2">
              <AlertTriangle className="w-4 h-4 text-gray-600" />
              <span className="text-gray-800 text-sm font-medium">
                {isOnline ? 'Sensor data not available' : 'Device offline'}
              </span>
            </div>
          </motion.div>
        )}
      </div>

      {/* Chart Modal */}
      {showChart && (
        <SensorChart
          sensorId={sensor.id}
          sensorName={sensor.name}
          sensorType={sensor.type}
          onClose={() => setShowChart(false)}
        />
      )}
    </>
  );
};

export default FuelTank;